/** Cinematic WebGL scene; the accessible phase controls remain in the Angular template. */
export interface OrbitaRendererState {
  selected: number;
  progress: number;
  paused: boolean;
  palette?: { primary: string; accent: string; background: string };
}

export interface OrbitaRenderer {
  update(state: Partial<OrbitaRendererState>): void;
  resize(): void;
  dispose(): void;
}

const VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// Analytic sphere intersections keep the atmosphere, procedural terrain and
// translucent rings affordable on integrated GPUs. No textures or network I/O.
const FRAGMENT_SHADER = `
precision highp float;

uniform vec2 u_resolution;
uniform vec2 u_pointer;
uniform float u_time;
uniform float u_rotation;
uniform float u_selected;
uniform float u_progress;
uniform vec3 u_primary;
uniform vec3 u_accent;
uniform vec3 u_background;

const float PI = 3.14159265359;
const float TAU = 6.28318530718;

float hash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float hash31(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}

float noise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash31(i), hash31(i + vec3(1, 0, 0)), f.x),
        mix(hash31(i + vec3(0, 1, 0)), hash31(i + vec3(1, 1, 0)), f.x), f.y),
    mix(mix(hash31(i + vec3(0, 0, 1)), hash31(i + vec3(1, 0, 1)), f.x),
        mix(hash31(i + vec3(0, 1, 1)), hash31(i + vec3(1, 1, 1)), f.x), f.y), f.z);
}

float fbm(vec3 p) {
  float sum = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 5; i++) {
    sum += amplitude * noise(p);
    p = p * 2.03 + vec3(13.1, 7.7, 5.3);
    amplitude *= 0.48;
  }
  return sum;
}

mat2 rotation(float a) {
  float s = sin(a), c = cos(a);
  return mat2(c, -s, s, c);
}

vec3 hue(vec3 c, float a) {
  vec3 k = vec3(0.57735027);
  return max(c * cos(a) + cross(k, c) * sin(a) + k * dot(k, c) * (1.0 - cos(a)), vec3(0.015));
}

float angularDistance(float a, float b) {
  return abs(mod(a - b + PI, TAU) - PI);
}

vec3 stars(vec2 p, float scale, float seed) {
  vec2 grid = p * scale;
  vec2 cell = floor(grid);
  float chance = hash21(cell + seed);
  vec2 offset = vec2(hash21(cell + seed + 2.2), hash21(cell + seed + 5.9));
  vec2 local = fract(grid) - 0.12 - offset * 0.76;
  float size = mix(0.014, 0.048, chance * chance);
  float core = exp(-dot(local, local) / (size * size));
  float twinkle = 0.82 + 0.18 * sin(u_time * 0.65 + chance * 51.0);
  float visible = step(0.87, chance);
  float flare = exp(-abs(local.x) * 130.0 - abs(local.y) * 12.0)
              + exp(-abs(local.y) * 130.0 - abs(local.x) * 12.0);
  float light = (core * mix(0.28, 1.2, chance) + flare * step(0.995, chance) * 0.18) * visible * twinkle;
  return mix(vec3(0.78, 0.86, 1.0), vec3(1.0, 0.87, 0.67), offset.x) * light;
}

vec3 space(vec2 p, vec3 tint) {
  vec2 drift = u_pointer * 0.025 + vec2(u_time * 0.0005, 0.0);
  float cloud = fbm(vec3(p * 1.45 + drift, 1.7));
  float ribbon = exp(-pow((p.y + p.x * 0.32 - 0.08) * 2.5, 2.0));
  vec3 color = u_background * 0.43;
  color += mix(u_primary, tint, cloud) * pow(cloud, 3.0) * ribbon * 0.32;
  color += tint * exp(-length(p - vec2(-0.34, 0.24)) * 4.0) * 0.035;
  color += stars(p + drift, 27.0, 7.3);
  color += stars(p + drift * 0.4, 59.0, 91.8) * 0.44;
  return color;
}

vec4 globe(vec2 p, vec2 center, float radius, vec3 tint) {
  vec2 q = (p - center) / radius;
  float r2 = dot(q, q);
  if (r2 > 1.0) return vec4(0.0);

  // Orthographic ray/sphere intersection: z is the illuminated front surface.
  vec3 normal = vec3(q, sqrt(max(0.0, 1.0 - r2)));
  vec3 surface = normal;
  surface.xy = rotation(-0.21) * surface.xy;
  surface.xz = rotation(u_time * 0.035 + u_rotation * 0.20) * surface.xz;

  float broad = fbm(surface * 3.2 + vec3(4.1, 2.7, 9.3));
  float ridges = fbm(surface * 19.0 + broad * 2.0);
  float continents = smoothstep(0.44, 0.57, broad + (ridges - 0.5) * 0.09);
  float coast = exp(-abs(broad - 0.48) * 85.0);
  float ice = smoothstep(0.76, 0.98, abs(surface.y) + broad * 0.10);

  vec3 ocean = mix(u_primary * 0.055, tint * 0.13, ridges);
  vec3 land = mix(tint * 0.18, vec3(0.29, 0.36, 0.37), ridges);
  vec3 albedo = mix(ocean, land, continents);
  albedo = mix(albedo, vec3(0.66, 0.80, 0.86), ice * 0.86);

  float phaseLight = (u_selected - 1.0) * 0.34;
  vec3 light = normalize(vec3(-0.72 + cos(phaseLight) * 0.09 + u_pointer.x * 0.12,
                              0.58 + sin(phaseLight) * 0.12 + u_pointer.y * 0.08, 0.76));
  float diffuse = max(0.0, dot(normal, light));
  float terminator = smoothstep(-0.12, 0.18, dot(normal, light));
  float specular = pow(max(dot(normal, normalize(light + vec3(0, 0, 1))), 0.0), 72.0);
  vec3 color = albedo * (0.08 + diffuse * 1.7);
  color += mix(vec3(0.50, 0.72, 0.95), tint, 0.32) * specular * (1.0 - continents) * 0.75;

  // A separate cloud shell turns at its own speed and casts soft terrain shade.
  vec3 cloudSurface = surface;
  cloudSurface.xz = rotation(u_time * 0.011) * cloudSurface.xz;
  float clouds = smoothstep(0.46, 0.69, fbm(cloudSurface * 7.2 + vec3(broad * 1.7)));
  color *= 1.0 - clouds * 0.21;
  color += vec3(0.67, 0.83, 0.92) * clouds * (0.06 + diffuse * 0.68);

  float cityNoise = noise(surface * 165.0);
  float cities = pow(max(0.0, (cityNoise - 0.60) / 0.4), 3.0) * continents;
  color += mix(vec3(1.0, 0.55, 0.20), tint, 0.35) * cities * (1.0 - terminator) * 1.4;
  color += tint * coast * (1.0 - terminator) * 0.035;

  float fresnel = pow(1.0 - normal.z, 3.8);
  color += mix(u_primary, tint, 0.32) * fresnel * (0.10 + diffuse * 0.85);
  color += vec3(0.36, 0.62, 1.0) * fresnel * pow(diffuse, 2.0) * 0.65;
  return vec4(color, 1.0 - smoothstep(0.995, 1.0, r2));
}

vec4 rings(vec2 p, vec2 center, vec3 tint) {
  vec2 q = rotation(-0.25) * (p - center);
  float radial = length(vec2(q.x, q.y / 0.37));
  float band = smoothstep(0.345, 0.358, radial) * (1.0 - smoothstep(0.475, 0.49, radial));
  float stripes = 0.34 + 0.34 * sin(radial * 970.0) + 0.18 * sin(radial * 347.0);
  float division = 1.0 - smoothstep(0.002, 0.008, abs(radial - 0.425));
  float angle = atan(q.y / 0.37, q.x);
  float light = 0.35 + 0.65 * max(0.0, cos(angle - 2.4));
  float litEdge = exp(-abs(radial - 0.358) * 520.0) + exp(-abs(radial - 0.480) * 650.0);
  vec3 ice = mix(vec3(0.31, 0.44, 0.54), tint, 0.30);
  vec3 color = ice * (0.45 + stripes * 0.55) * light;
  color += tint * litEdge * 0.60;
  float alpha = band * (0.28 + stripes * 0.44) * (1.0 - division * 0.88);
  alpha = max(alpha, litEdge * 0.45);
  return vec4(color, clamp(alpha, 0.0, 0.75));
}

vec3 orbitalPath(vec2 p, vec2 dimensions, vec3 tint) {
  vec2 q = p / (dimensions * vec2(0.40, 0.32));
  float radial = length(q);
  float distance = abs(radial - 1.0);
  float angle = atan(-q.y, q.x);
  float arc = mod(angle - PI * 0.5 + TAU, TAU);
  float complete = 1.0 - smoothstep(u_progress * TAU, u_progress * TAU + 0.015, arc);
  float line = exp(-distance * 600.0);
  float dotted = step(0.46, fract(angle * 36.0 / TAU));
  float pulse = exp(-angularDistance(angle, u_time * 0.17 + u_rotation) * 13.0);
  vec3 color = tint * line * (0.12 + dotted * 0.055 + complete * 0.2 + pulse * 0.7);
  color += tint * exp(-distance * 60.0) * (0.013 + pulse * 0.06);

  // Fine cartographic arcs give the planet scale without competing with nodes.
  vec2 outer = rotation(0.26) * p;
  float shell = abs(length(outer / (dimensions * vec2(0.47, 0.38))) - 1.0);
  color += u_primary * exp(-shell * 730.0) * 0.11;
  return color;
}

vec3 satellites(vec3 background, vec2 p, vec2 dimensions, vec3 tint) {
  vec3 color = background;
  for (int i = 0; i < 6; i++) {
    float index = float(i);
    float angle = index * TAU / 6.0 - u_rotation + PI * 0.5;
    vec2 center = vec2(cos(angle), -sin(angle)) * dimensions * vec2(0.40, 0.32);
    float depth = 0.5 + 0.5 * sin(angle);
    float radius = mix(0.024, 0.038, depth);
    vec2 q = (p - center) / radius;
    float r2 = dot(q, q);
    float selected = 1.0 - step(0.5, abs(index + 1.0 - u_selected));
    vec3 localTint = hue(mix(u_primary, tint, 0.45), index * 0.36 - 0.44);
    float halo = exp(-max(length(q) - 1.0, 0.0) * 4.3);
    color += localTint * halo * (0.07 + selected * 0.24);

    // Selection belongs to the world: a quiet energy field around that moon.
    float targetRing = exp(-abs(length(q) - 1.55) * 36.0);
    color += tint * targetRing * selected * (0.45 + 0.10 * sin(u_time * 1.2));

    if (r2 < 1.0) {
      vec3 normal = vec3(q, sqrt(max(0.0, 1.0 - r2)));
      vec3 surface = normal;
      surface.xz = rotation(u_time * (0.022 + index * 0.004)) * surface.xz;
      float terrain = fbm(surface * (3.0 + index * 0.9) + index * 11.0);
      float gas = 0.5 + 0.5 * sin(surface.y * 23.0 + terrain * 8.0);
      float texture = mix(terrain, gas, step(2.5, index));
      vec3 mineral = mix(localTint * 0.23, vec3(0.52, 0.51, 0.47), texture * 0.72);
      float light = max(0.0, dot(normal, normalize(vec3(-0.7, 0.55, 0.8))));
      vec3 sphere = mineral * (0.12 + light * 1.6);
      sphere += localTint * pow(1.0 - normal.z, 3.0) * (0.12 + light * 0.65);
      color = mix(color, sphere, 1.0 - smoothstep(0.975, 1.0, r2));
    }
  }
  return color;
}

void main() {
  float side = min(u_resolution.x, u_resolution.y);
  vec2 dimensions = u_resolution / side;
  vec2 p = (gl_FragCoord.xy - u_resolution * 0.5) / side;
  vec3 tint = hue(mix(u_primary, u_accent, 0.74), (u_selected - 1.0) * 0.115);
  vec2 center = vec2(0.0, 0.02 * dimensions.y) + u_pointer * vec2(0.012, 0.009);
  vec3 color = space(p, tint);
  color += orbitalPath(p, dimensions, tint);

  float radius = 0.245;
  vec2 delta = p - center;
  float distance = length(delta);
  vec2 lightDirection = normalize(vec2(-0.7, 0.55));
  float illuminated = 0.30 + 0.70 * max(0.0, dot(normalize(delta + vec2(0.00001)), lightDirection));
  float outside = max(distance - radius, 0.0);
  float atmosphere = exp(-outside * 94.0) * step(radius, distance);
  color += tint * atmosphere * illuminated * 0.24;
  color += u_primary * exp(-outside * 21.0) * step(radius, distance) * 0.028;

  vec4 ring = rings(p, center, tint);
  vec2 ringPlane = rotation(-0.25) * (p - center);
  float front = 1.0 - step(0.0, ringPlane.y);
  color = mix(color, ring.rgb + color * 0.35, ring.a * (1.0 - front));
  vec4 planet = globe(p, center, radius, tint);
  color = mix(color, planet.rgb, planet.a);
  color = mix(color, ring.rgb + color * 0.55, ring.a * front);
  color = satellites(color, p, dimensions, tint);

  float vignette = 1.0 - smoothstep(0.38, 0.95, length(p / dimensions));
  color *= 0.62 + vignette * 0.38;
  color = vec3(1.0) - exp(-color * 1.26);
  color = pow(max(color, vec3(0.0)), vec3(0.90));
  color += (hash21(gl_FragCoord.xy + fract(u_time) * 17.0) - 0.5) * 0.004;
  gl_FragColor = vec4(max(color, vec3(0.0)), 1.0);
}
`;

type Color = [number, number, number];
type Uniforms = Record<string, WebGLUniformLocation | null>;

function finite(value: number, fallback: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
}

/** CSS custom properties in the theme resolve to hex or rgb values. */
function color(value: string, fallback: Color): Color {
  const hex = value.trim().match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];
  if (hex) {
    const expanded = hex.length === 3 ? [...hex].map(n => n + n).join('') : hex;
    return [0, 2, 4].map(index => Number.parseInt(expanded.slice(index, index + 2), 16) / 255) as Color;
  }
  const rgb = value.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  return rgb ? [1, 2, 3].map(index => Math.min(255, Number(rgb[index])) / 255) as Color : fallback;
}

/**
 * The caller creates this outside Angular's zone. A null result keeps the CSS
 * fallback in charge on devices with WebGL disabled or unavailable.
 */
export function createOrbitaRenderer(canvas: HTMLCanvasElement, initial: OrbitaRendererState): OrbitaRenderer | null {
  if (typeof WebGLRenderingContext === 'undefined') return null;
  let gl: WebGLRenderingContext | null;
  try {
    gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
  } catch {
    return null;
  }
  if (!gl) return null;
  const context = gl;

  let state: OrbitaRendererState = {
    ...initial,
    selected: finite(initial.selected, 1, 1, 6),
    progress: finite(initial.progress, 0, 0, 100),
  };
  let program: WebGLProgram | null = null;
  let buffer: WebGLBuffer | null = null;
  let uniforms: Uniforms = {};
  let frame = 0;
  let disposed = false;
  let lost = false;
  let elapsed = 0;
  let lastTimestamp = 0;
  let lastPaint = 0;
  let angle = (state.selected - 1) * Math.PI / 3;
  let pointerX = 0;
  let pointerY = 0;
  let targetX = 0;
  let targetY = 0;
  let primary: Color = [0, 0, 0];
  let accent: Color = [0, 0, 0];
  let background: Color = [0, 0, 0];

  function readPalette(): void {
    const style = getComputedStyle(canvas);
    primary = color(state.palette?.primary ?? style.getPropertyValue('--mg-primary'), primary);
    accent = color(state.palette?.accent ?? style.getPropertyValue('--mg-acento'), accent);
    background = color(state.palette?.background ?? style.getPropertyValue('--mg-shell'), background);
  }

  function release(): void {
    if (buffer) context.deleteBuffer(buffer);
    if (program) context.deleteProgram(program);
    buffer = null;
    program = null;
  }

  function shader(type: number, source: string): WebGLShader | null {
    const result = context.createShader(type);
    if (!result) return null;
    context.shaderSource(result, source);
    context.compileShader(result);
    if (!context.getShaderParameter(result, context.COMPILE_STATUS)) {
      context.deleteShader(result);
      return null;
    }
    return result;
  }

  function initialize(): boolean {
    const vertex = shader(context.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = shader(context.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) {
      if (vertex) context.deleteShader(vertex);
      if (fragment) context.deleteShader(fragment);
      return false;
    }
    program = context.createProgram();
    if (!program) {
      context.deleteShader(vertex);
      context.deleteShader(fragment);
      return false;
    }
    context.attachShader(program, vertex);
    context.attachShader(program, fragment);
    context.linkProgram(program);
    context.deleteShader(vertex);
    context.deleteShader(fragment);
    if (!context.getProgramParameter(program, context.LINK_STATUS)) {
      release();
      return false;
    }

    buffer = context.createBuffer();
    if (!buffer) {
      release();
      return false;
    }
    context.useProgram(program);
    context.bindBuffer(context.ARRAY_BUFFER, buffer);
    context.bufferData(context.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), context.STATIC_DRAW);
    const position = context.getAttribLocation(program, 'a_position');
    context.enableVertexAttribArray(position);
    context.vertexAttribPointer(position, 2, context.FLOAT, false, 0, 0);
    uniforms = {};
    for (const name of ['resolution', 'pointer', 'time', 'rotation', 'selected', 'progress', 'primary', 'accent', 'background']) {
      uniforms[name] = context.getUniformLocation(program, `u_${name}`);
    }
    return true;
  }

  function render(): void {
    if (disposed || lost || !program) return;
    canvas.parentElement?.style.setProperty('--orbita-rotacao', `${angle * 180 / Math.PI}deg`);
    context.useProgram(program);
    context.viewport(0, 0, canvas.width, canvas.height);
    context.uniform2f(uniforms['resolution'], canvas.width, canvas.height);
    context.uniform2f(uniforms['pointer'], pointerX, pointerY);
    context.uniform1f(uniforms['time'], elapsed);
    context.uniform1f(uniforms['rotation'], angle);
    context.uniform1f(uniforms['selected'], state.selected);
    context.uniform1f(uniforms['progress'], state.progress / 100);
    context.uniform3fv(uniforms['primary'], primary);
    context.uniform3fv(uniforms['accent'], accent);
    context.uniform3fv(uniforms['background'], background);
    context.drawArrays(context.TRIANGLE_STRIP, 0, 4);
  }

  function resize(): void {
    if (disposed || lost) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    render();
  }

  function tick(timestamp: number): void {
    frame = 0;
    if (disposed || lost || state.paused) return;
    if (!lastTimestamp) lastTimestamp = timestamp;
    const delta = Math.min((timestamp - lastTimestamp) / 1000, 0.08);
    elapsed += delta;
    lastTimestamp = timestamp;
    const response = 1.0 - Math.exp(-delta * 7.0);
    const target = (state.selected - 1) * Math.PI / 3;
    const difference = Math.atan2(Math.sin(target - angle), Math.cos(target - angle));
    angle += difference * response;
    pointerX += (targetX - pointerX) * response;
    pointerY += (targetY - pointerY) * response;
    // Slow celestial motion reads smoothly at 30 fps, including on laptop GPUs.
    if (timestamp - lastPaint >= 1000 / 30) {
      render();
      lastPaint = timestamp;
    }
    frame = requestAnimationFrame(tick);
  }

  function schedule(): void {
    if (!frame && !disposed && !lost && !state.paused) {
      lastTimestamp = 0;
      frame = requestAnimationFrame(tick);
    }
  }

  function update(patch: Partial<OrbitaRendererState>): void {
    if (disposed) return;
    state = {
      ...state,
      ...patch,
      selected: finite(patch.selected ?? state.selected, state.selected, 1, 6),
      progress: finite(patch.progress ?? state.progress, state.progress, 0, 100),
    };
    if (patch.palette) readPalette();
    if (state.paused) {
      cancelAnimationFrame(frame);
      frame = 0;
      angle = (state.selected - 1) * Math.PI / 3;
      pointerX = 0;
      pointerY = 0;
    }
    render();
    schedule();
  }

  const pointerSurface = canvas.parentElement ?? canvas;
  function pointer(event: PointerEvent): void {
    if (state.paused) return;
    const rect = canvas.getBoundingClientRect();
    targetX = finite((event.clientX - rect.left) / rect.width * 2 - 1, 0, -1, 1);
    targetY = finite(1 - (event.clientY - rect.top) / rect.height * 2, 0, -1, 1);
  }
  function pointerLeave(): void {
    targetX = 0;
    targetY = 0;
  }
  function contextLost(event: Event): void {
    event.preventDefault();
    lost = true;
    cancelAnimationFrame(frame);
    frame = 0;
  }
  function contextRestored(): void {
    if (disposed) return;
    try {
      release();
      lost = !initialize();
    } catch {
      release();
      lost = true;
    }
    if (!lost) {
      readPalette();
      resize();
      schedule();
    }
  }

  try {
    if (!initialize()) return null;
  } catch {
    release();
    return null;
  }
  readPalette();
  resize();
  schedule();
  pointerSurface.addEventListener('pointermove', pointer, { passive: true });
  pointerSurface.addEventListener('pointerleave', pointerLeave, { passive: true });
  window.addEventListener('resize', resize, { passive: true });
  canvas.addEventListener('webglcontextlost', contextLost);
  canvas.addEventListener('webglcontextrestored', contextRestored);

  return {
    update,
    resize,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(frame);
      pointerSurface.removeEventListener('pointermove', pointer);
      pointerSurface.removeEventListener('pointerleave', pointerLeave);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('webglcontextlost', contextLost);
      canvas.removeEventListener('webglcontextrestored', contextRestored);
      canvas.parentElement?.style.removeProperty('--orbita-rotacao');
      release();
    },
  };
}
